import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  InputNumber,
  Select,
  Skeleton,
  message,
  Space,
} from "antd";
import { CheckOutlined, StopOutlined } from "@ant-design/icons";
import api from "../api/axios";
import { fetchProducts } from "../utils/fetchProducts";
import { useAuth } from "../contexts/AuthProvider";
import { hasPermission } from "../utils/auth";

const FormatSuggestions = () => {
  const { user } = useAuth();
  const canWriteOrders = hasPermission(user?.permissions, "orders:write");

  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [rowState, setRowState] = useState({});
  const [approvingId, setApprovingId] = useState(null);
  const [ignoringId, setIgnoringId] = useState(null);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const [suggestionsRes, productsData] = await Promise.all([
          api.get("/orders/format-suggestions/pending"),
          fetchProducts(),
        ]);
        const data = suggestionsRes.data;
        setSuggestions(data);
        const initial = {};
        data.forEach((s) => {
          initial[s.id] = {
            format: s.extracted_format ?? "",
            product_id: s.suggested_product_id ?? undefined,
            quantity: 1,
          };
        });
        setRowState(initial);
        setProducts(productsData);
      } catch (error) {
        message.error("Error al cargar los datos.");
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  const productOptions = products.map((p) => ({
    value: p.product_id,
    label: `${p.name} (ID: ${p.product_id})`,
  }));

  const updateRow = (id, field, value) => {
    setRowState((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleApprove = async (suggestion) => {
    const state = rowState[suggestion.id];
    setApprovingId(suggestion.id);
    setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
    try {
      await api.post(`/orders/format-suggestions/${suggestion.id}/approve`, {
        format: state.format || undefined,
        product_id: state.product_id || undefined,
        quantity: state.quantity || undefined,
      });
      message.success("Sugerencia aprobada.");
    } catch (error) {
      message.error("Error al aprobar la sugerencia.");
      setSuggestions((prev) => [...prev, suggestion]);
    } finally {
      setApprovingId(null);
    }
  };

  const handleIgnore = async (suggestion) => {
    setIgnoringId(suggestion.id);
    setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
    try {
      await api.post(`/orders/format-suggestions/${suggestion.id}/ignore`);
      message.success("Sugerencia ignorada.");
    } catch (error) {
      message.error("Error al ignorar la sugerencia.");
      setSuggestions((prev) => [...prev, suggestion]);
    } finally {
      setIgnoringId(null);
    }
  };

  const skeletonCell = {
    render: () => <Skeleton active title={false} paragraph={{ rows: 1 }} />,
  };

  const columns = [
    {
      title: "Título Shopify",
      dataIndex: "shopify_item_title",
      width: 220,
    },
    {
      title: "Variante Shopify",
      dataIndex: "shopify_variant_title",
      width: 130,
    },
    {
      title: "Aroma",
      dataIndex: "extracted_scent",
      width: 100,
    },
    {
      title: "Producto",
      width: 240,
      render: (_, row) => (
        <Select
          showSearch
          allowClear
          placeholder="Buscar producto..."
          value={rowState[row.id]?.product_id}
          onChange={(val) => updateRow(row.id, "product_id", val)}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={productOptions}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Formato",
      width: 130,
      render: (_, row) => (
        <Input
          value={rowState[row.id]?.format}
          onChange={(e) => updateRow(row.id, "format", e.target.value)}
          placeholder="ej. 20 kg"
        />
      ),
    },
    {
      title: "Cant.",
      width: 80,
      render: (_, row) => (
        <InputNumber
          min={1}
          value={rowState[row.id]?.quantity}
          onChange={(val) => updateRow(row.id, "quantity", val)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Creado",
      dataIndex: "created_at",
      width: 120,
      render: (val) =>
        new Date(val).toLocaleDateString("es-CL", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
    },
    {
      title: "Acciones",
      width: 150,
      render: (_, row) =>
        canWriteOrders && (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              disabled={!rowState[row.id]?.product_id}
              loading={approvingId === row.id}
              onClick={() => handleApprove(row)}
            >
              Aprobar
            </Button>
            <Button
              size="small"
              icon={<StopOutlined />}
              danger
              loading={ignoringId === row.id}
              onClick={() => handleIgnore(row)}
            >
              Ignorar
            </Button>
          </Space>
        ),
    },
  ];

  const skeletonColumns = columns.map((col) => ({ ...col, ...skeletonCell }));

  return (
    <div className="bg-white dark:bg-main-dark-bg rounded-lg p-4">
      <h2 className="text-lg font-semibold mb-4">
        Sugerencias de formato pendientes
      </h2>
      <Table
        columns={loading ? skeletonColumns : columns}
        dataSource={
          loading
            ? Array.from({ length: 5 }).map((_, i) => ({ key: i }))
            : suggestions.map((s) => ({ ...s, key: s.id }))
        }
        pagination={false}
        locale={{ emptyText: "No hay sugerencias pendientes." }}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default FormatSuggestions;
